import { env } from "../../env";
import { initKeycloak } from "../auth/keycloak";

export interface WorkorderReportRequest {
  workorderId: string;
  workorderRef: string;
  languageIsoCode?: string;
  includeImages?: boolean;
  compactLayout?: boolean;
}

/**
 * Filename for a workorder report: {CODE_LANG}-{existing workorder ref}.pdf
 * Only the language code is added — the ref (e.g. INTER-260313-1) is used as-is.
 */
export function getWorkorderReportFilename(
  languageIsoCode: string,
  workorderRef: string
): string {
  const lang = languageIsoCode.split("_")[0].split("-")[0].toUpperCase();
  return `${lang}-${workorderRef}.pdf`;
}

/**
 * Fallback filename when Content-Disposition is missing from the API response.
 */
export function resolveWorkorderReportFilename(
  workorderRef: string,
  languageIsoCode?: string
): string {
  return languageIsoCode
    ? getWorkorderReportFilename(languageIsoCode, workorderRef)
    : `${workorderRef}.pdf`;
}

/**
 * Build the API path for a workorder report download.
 */
export function buildWorkorderReportDownloadPath({
  workorderId,
  languageIsoCode,
  includeImages = true,
  compactLayout = true,
}: Pick<WorkorderReportRequest, "workorderId" | "languageIsoCode" | "includeImages" | "compactLayout">): string {
  const params = new URLSearchParams({
    includeImages: String(includeImages),
    compactLayout: String(compactLayout),
  });

  if (languageIsoCode) {
    params.set("langue", languageIsoCode);
  }

  return `admin/workorders/${workorderId}/report/download?${params.toString()}`;
}

/**
 * Parse filename from Content-Disposition response header.
 *
 * Kept for reference/tests only: the report filename is deliberately NOT derived
 * from this header. After a preview, the backend hands back a temporary
 * inline artifact name in Content-Disposition, which would otherwise leak into
 * the subsequent download. The client-computed name is authoritative instead
 * (see loadWorkorderReportFile).
 */
export function parseContentDispositionFilename(header: string | null): string | null {
  if (!header) return null;

  const utf8Match = header.match(/filename\*=UTF-8''([^;]+)/i);
  if (utf8Match) return decodeURIComponent(utf8Match[1]);

  const quotedMatch = header.match(/filename="([^"]+)"/i);
  if (quotedMatch) return quotedMatch[1];

  const plainMatch = header.match(/filename=([^;]+)/i);
  if (plainMatch) return plainMatch[1].trim();

  return null;
}

async function fetchWorkorderReportPdf(apiPath: string): Promise<ArrayBuffer> {
  const realm = localStorage.getItem("realm");
  const token = initKeycloak().token;
  const response = await fetch(`${env.GATEWAY_URL}/${realm}/${apiPath}`, {
    headers: token ? { Authorization: `Bearer ${token}` } : {},
  });

  if (!response.ok) {
    throw new Error(`Failed to fetch report (${response.status})`);
  }

  return response.arrayBuffer();
}

function createWorkorderReportFile(response: ArrayBuffer, filename: string): File {
  return new File([response], filename, { type: "application/pdf" });
}

/**
 * Fetch a workorder report and return a named PDF File.
 *
 * The filename is always the deterministic client-computed name so that preview
 * and download stay consistent. It intentionally does NOT use the server's
 * Content-Disposition: after a preview the backend returns a temporary artifact
 * name there, which previously leaked into the downloaded file's name.
 */
export async function loadWorkorderReportFile(
  request: WorkorderReportRequest
): Promise<File> {
  const apiPath = buildWorkorderReportDownloadPath(request);
  const filename = resolveWorkorderReportFilename(
    request.workorderRef,
    request.languageIsoCode
  );
  const data = await fetchWorkorderReportPdf(apiPath);
  return createWorkorderReportFile(data, filename);
}

function writePreviewLoadingPage(previewWindow: Window): void {
  previewWindow.document.open();
  previewWindow.document.write(`<!DOCTYPE html>
<html lang="fr">
<head>
  <meta charset="utf-8" />
  <title>Chargement...</title>
  <style>
    html, body { margin: 0; height: 100%; font-family: system-ui, sans-serif; }
    body { display: flex; align-items: center; justify-content: center; color: #374151; }
  </style>
</head>
<body>Chargement du rapport...</body>
</html>`);
  previewWindow.document.close();
}

/**
 * Open a workorder report PDF in the browser's native PDF viewer (new tab).
 */
export async function previewWorkorderReport(request: WorkorderReportRequest): Promise<void> {
  const previewWindow = window.open("about:blank", "_blank");

  if (!previewWindow) {
    throw new Error("Popup blocked");
  }

  writePreviewLoadingPage(previewWindow);

  try {
    const file = await loadWorkorderReportFile(request);
    const pdfUrl = URL.createObjectURL(file);
    previewWindow.location.href = pdfUrl;
    previewWindow.addEventListener("beforeunload", () => URL.revokeObjectURL(pdfUrl));
  } catch (error) {
    previewWindow.close();
    throw error;
  }
}

/**
 * Download a workorder report PDF with the correct filename.
 */
export async function downloadWorkorderReport(request: WorkorderReportRequest): Promise<void> {
  const file = await loadWorkorderReportFile(request);
  const url = URL.createObjectURL(file);
  const link = document.createElement("a");
  link.href = url;
  link.download = file.name;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}
